import React, { useState } from 'react';
import {
  Dialog,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGetFilteredAgreementsQuery } from 'store/agreements/agreements';
import { useParams, useSearchParams } from 'react-router-dom';
import headers from 'components/EditEmployeeForm/Agreements/headers';
import RowAddButton from 'common/RowAddButton/RowAddButton';
import AddEmployeeAgreement from 'components/EditEmployeeForm/Agreements/AddEmployeeAgreement';
import { Agreement } from 'store/agreements/interfaces';
import emptyApiResponse from 'store/emptyApiResponse';
import usePagination from 'hooks/usePagination';
import CustomPagination from 'common/CustomPagination/CustomPagination';
import TableBodyContent from 'common/TableBodyContent/TableBodyContent';
import AgreementItem from './AgreementItem/AgreementItem';

function Agreements() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams({});
  const { page, handleOnPageChange, handleOnItemsPerPageChange, itemsPerPage } =
    usePagination({
      searchParams,
      setSearchParams,
    });

  const {
    data: agreementsData = {
      'hydra:member': [] as Agreement[],
      ...emptyApiResponse,
    },
    isFetching: agreementsFetching,
    isError: agreementsError,
  } = useGetFilteredAgreementsQuery({
    employee: id,
    page,
    itemsPerPage,
  });
  const { t } = useTranslation();

  return (
    <>
      <TableContainer sx={{ minHeight: 'calc(100vh - 30rem)' }}>
        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header.label} align={header.align}>
                  <Typography variant="tableHeader" color="text.secondary">
                    {t(header.label)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell colSpan={headers.length} />
            </TableRow>
          </TableHead>
          <TableBodyContent
            isError={agreementsError}
            isFetching={agreementsFetching}
            tableHeaders={headers}
          >
            <RowAddButton
              onClick={() => setIsAddDialogOpen(true)}
              colSpan={headers.length + 1}
            >
              <Typography variant="body2">{t('agreements.add_new')}</Typography>
            </RowAddButton>
            {agreementsData['hydra:member'].map((agreement) => (
              <AgreementItem key={agreement.id} agreement={agreement} />
            ))}
          </TableBodyContent>
        </Table>
      </TableContainer>
      {agreementsData?.['hydra:view']?.['hydra:last'] && (
        <CustomPagination
          apiData={agreementsData}
          itemsPerPage={itemsPerPage}
          page={page}
          handleOnPageChange={handleOnPageChange}
          handleOnItemsPerPageChange={handleOnItemsPerPageChange}
          showRowsSelect={false}
        />
      )}
      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)}>
        <AddEmployeeAgreement
          setIsOpen={setIsAddDialogOpen}
          employeeId={Number(id)}
        />
      </Dialog>
    </>
  );
}

export default Agreements;
